import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { GradeSection } from './components/GradeSection';
import { ReaderModal } from './components/ReaderModal';
import { ContactSection } from './components/ContactSection';
import { LoginModal } from './components/LoginModal';
import { grades, platformFeatures, type Resource, type User } from './lib/mockData';

export default function App() {
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    // Check localStorage for saved user
    const savedUser = localStorage.getItem('currentUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Apply dark mode class to document root
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleLogin = (user: User) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  const handleSignOut = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  // Show login modal if user is not authenticated
  if (!currentUser) {
    return <LoginModal onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Header 
        isDarkMode={isDarkMode} 
        onToggleTheme={toggleTheme}
        currentUser={currentUser}
        onSignOut={handleSignOut}
      />

      {/* Hero Section */}
      <section id="home" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-green-600 dark:from-blue-900 dark:to-green-900 opacity-10"></div>
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-lg inline-block mb-4">
                <p className="text-sm font-semibold">
                  Welcome, {currentUser.name}! ({currentUser.role === 'student' ? '👨‍🎓 Student' : '👨‍🏫 Teacher'})
                </p>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                Welcome to
                <span className="block text-blue-600 dark:text-blue-400">Open Class Ug</span>
              </h1>
              <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                Your digital learning platform for secondary school education. 
                Access textbooks, assignments, and resources aligned with Uganda's new curriculum.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('learning');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  {currentUser.role === 'teacher' ? 'View Available Content / Set Assignment' : 'Start Learning'}
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('about');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 text-blue-600 dark:text-blue-400 px-8 py-3 rounded-lg font-semibold text-lg transition-colors shadow-lg border-2 border-blue-600 dark:border-blue-400"
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://cdn.pixabay.com/photo/2018/09/11/19/49/education-3670453_640.jpg"
                alt="Students learning in classroom"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Platform Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800 dark:text-white">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Sections */}
      <section id="learning" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Learning Sections
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Select your grade level to access textbooks and assignments based on the new curriculum
            </p>
          </div>
          
          <div className="space-y-6 max-w-6xl mx-auto">
            {grades.map((grade) => (
              <GradeSection
                key={`${grade.id}-${refreshKey}`}
                grade={grade}
                onOpenResource={setSelectedResource}
                currentUser={currentUser}
                onResourceAdded={() => setRefreshKey(prev => prev + 1)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
              About Open Class Ug
            </h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src="https://cdn.pixabay.com/photo/2021/01/21/15/54/books-5937716_640.jpg"
                  alt="Textbooks and learning materials"
                  className="rounded-xl shadow-lg w-full"
                />
              </div>
              <div className="space-y-4">
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  Open Class Ug is a digital learning platform designed specifically for secondary 
                  school students and teachers in Uganda. We provide easy access to educational 
                  resources aligned with the new curriculum.
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  Our mission is to make quality education accessible to all students, regardless 
                  of their location. With digital textbooks, assignments, and study materials, 
                  learning has never been more convenient.
                </p>
                <div className="bg-blue-50 dark:bg-gray-800 border-l-4 border-blue-600 dark:border-blue-400 p-4 rounded">
                  <p className="text-gray-800 dark:text-gray-200 font-medium">
                    💡 All resources are based on Uganda's new curriculum and are available 
                    for free to students and teachers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Open Class Ug</h3>
              <p className="text-gray-400 dark:text-gray-500">
                Digital learning platform for secondary school education in Uganda
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('home');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('learning');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
                  >
                    Learning Sections
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('about');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
                  >
                    About
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Subjects Available</h3>
              <ul className="text-gray-400 dark:text-gray-500 space-y-2">
                <li>📐 Mathematics</li>
                <li>📝 English Language</li>
                <li>🔬 Science (Physics, Chemistry, Biology)</li>
                <li>📚 Literature</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 dark:border-gray-900 pt-8 text-center text-gray-400 dark:text-gray-500">
            <p>&copy; 2025 Open Class Ug. Making education accessible for all.</p>
          </div>
        </div>
      </footer>

      {/* Reader Modal */}
      <ReaderModal
        resource={selectedResource}
        onClose={() => setSelectedResource(null)}
      />
    </div>
  );
}
